import { saveAs } from 'file-saver';

export const exportToTxt = (data, filename) => {
  const pi = data.personalInfo || {};
  const fmtDate = (d) => {
    if (!d) return 'Present';
    const [y, m] = d.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return m ? `${months[parseInt(m) - 1]} ${y}` : y;
  };
  const hr = '-'.repeat(40);
  const lines = [];

  if (pi.fullName) lines.push(pi.fullName.toUpperCase());
  if (pi.jobTitle) lines.push(pi.jobTitle);
  const contact = [pi.email, pi.phone, pi.location, pi.linkedin, pi.website].filter(Boolean);
  if (contact.length) lines.push(contact.join(' | '));

  if (pi.summary) {
    lines.push('', (pi.summaryTitle || 'PROFESSIONAL SUMMARY').toUpperCase(), hr, pi.summary);
  }

  if (data.experience?.length) {
    lines.push('', 'EXPERIENCE', hr);
    data.experience.forEach(e => {
      lines.push(`${e.title} — ${e.company}${e.location ? ` · ${e.location}` : ''}`);
      lines.push(`${fmtDate(e.startDate)} – ${e.current ? 'Present' : fmtDate(e.endDate)}`);
      if (e.description) lines.push(e.description);
      (e.bullets || []).filter(Boolean).forEach(b => lines.push(`• ${b}`));
      lines.push('');
    });
  }

  if (data.education?.length) {
    lines.push('EDUCATION', hr);
    data.education.forEach(edu => {
      lines.push(`${edu.degree} — ${edu.school}`);
      lines.push(`${fmtDate(edu.startDate)} – ${fmtDate(edu.endDate)}${edu.gpa ? ` | GPA: ${edu.gpa}` : ''}`);
      lines.push('');
    });
  }

  if (data.skills?.length) {
    lines.push('SKILLS', hr);
    data.skills.forEach(s => lines.push(`${s.category}: ${s.items}`));
    lines.push('');
  }

  if (data.projects?.length) {
    lines.push('PROJECTS', hr);
    data.projects.forEach(p => {
      lines.push(p.title + (p.date ? ` (${fmtDate(p.date)})` : ''));
      if (p.skills) lines.push(`Tech: ${p.skills}`);
      if (p.summary) lines.push(p.summary);
      (p.bullets || []).filter(Boolean).forEach(b => lines.push(`• ${b}`));
      lines.push('');
    });
  }

  if (data.achievements?.length) {
    lines.push('ACHIEVEMENTS', hr);
    data.achievements.forEach(a => {
      lines.push(`${a.title}${a.date ? ` (${fmtDate(a.date)})` : ''}: ${a.description}`);
    });
    lines.push('');
  }

  if (data.certifications?.length) {
    lines.push('CERTIFICATIONS', hr);
    data.certifications.forEach(c => lines.push(`${c.name} — ${c.issuer}${c.date ? `, ${fmtDate(c.date)}` : ''}`));
    lines.push('');
  }

  if (data.languages?.length) {
    lines.push('LANGUAGES', hr);
    data.languages.forEach(l => lines.push(`${l.language}${l.proficiency ? ` — ${l.proficiency}` : ''}`));
    lines.push('');
  }

  if (data.hobbies) {
    lines.push('HOBBIES & INTERESTS', hr, data.hobbies, '');
  }

  saveAs(new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' }), `${filename}.txt`);
};

// Resume is always rendered at 794 px wide (A4 at 96 dpi).
// Passing this explicitly to html2canvas prevents it from guessing the
// viewport width from the browser window, which can be narrower when the
// builder panel is open.
const RESUME_NATURAL_W = 794;

// Multi-page resumes push section/sidebar items away from page-break seams via
// an async layout pass (see the pagination effect in ResumePreview.jsx), which
// can take up to ~1.2s to settle after the resume mounts or its data changes.
// Capturing before it settles leaves content flush against a page edge with no
// reserved margin, so wait (bounded) for it to finish before rendering the PDF.
const waitForPaginationReady = async (el, timeoutMs = 1600) => {
  const start = Date.now();
  while (el.dataset.paginationReady === 'false' && Date.now() - start < timeoutMs) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
};

export const exportToPDF = async (elementId, filename, options = {}) => {
  const html2pdf = (await import('html2pdf.js')).default;
  const el = document.getElementById(elementId);
  if (!el) return;
  await waitForPaginationReady(el);
  const format = options.pageSize === 'letter' ? 'letter' : 'a4';
  const fullHeight = el.scrollHeight;

  await html2pdf().set({
    margin: 0,
    filename: `${filename}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      // Fix a known html2canvas bug where scroll offset is applied twice.
      scrollX: 0,
      scrollY: 0,
      // Lock the capture to the resume's natural width so the viewport width
      // of the browser window does not affect layout or line-wrapping.
      width: RESUME_NATURAL_W,
      windowWidth: RESUME_NATURAL_W,
      height: fullHeight,
      windowHeight: fullHeight,
      ignoreElements: (node) =>
        node.getAttribute && node.getAttribute('data-html2canvas-ignore') === 'true',
      onclone: (doc) => {
        const clone = doc.getElementById(elementId);
        if (!clone) return;
        // Strip decorative styles that don't belong in the PDF.
        clone.style.boxShadow = 'none';
        clone.style.borderRadius = '0';
        // Neutralise any CSS transform on ancestor elements.
        // The live preview scales the resume to fit the panel via transform:scale(),
        // which confuses html2canvas into rendering at the wrong size/position.
        let parent = clone.parentElement;
        while (parent && parent.tagName !== 'BODY') {
          if (parent.style.transform) parent.style.transform = 'none';
          if (parent.style.position === 'absolute') parent.style.position = 'relative';
          parent = parent.parentElement;
        }
      },
    },
    jsPDF: { unit: 'mm', format, orientation: 'portrait' },
    // 'legacy' slices the rendered canvas at page boundaries.
    // 'css' mode is excluded because it mutates the DOM with page-break-before
    // declarations that produce layout artifacts in some templates.
    pagebreak: { mode: ['legacy'] },
  }).from(el).save();
};

